import { useTranslation } from 'react-i18next';
import type { MethodContent, RiskItem } from '@/types/methodologies';

interface RiskRegisterViewProps {
  /** Unused — risk rows carry no per-methodology section keys; kept for the shared view prop shape. */
  methodId: string;
  content: MethodContent;
}

type RiskLevel = 'low' | 'medium' | 'high';

/** Same bands as `riskLevel` in backend/src/methodologies/base.ts: 1–7 low, 8–14 medium, 15–25 high. */
export function riskLevel(score: number): RiskLevel {
  if (score >= 15) return 'high';
  if (score >= 8) return 'medium';
  return 'low';
}

const LEVEL_BG: Record<RiskLevel, string> = {
  high: 'bg-[#fdeceb]',
  medium: 'bg-[#fdf3e3]',
  low: 'bg-[#eaf7ee]',
};

function ScoreBadge({ risk }: { risk: RiskItem }) {
  const { t } = useTranslation('methodologies');
  const level = riskLevel(risk.score);
  return (
    <span
      className={`${LEVEL_BG[level]} inline-flex flex-col items-center rounded-[8px] px-[8px] py-[4px] min-w-[56px]`}
      title={t(`risk.levels.${level}`)}
    >
      <span className="font-manrope font-bold text-[16px] leading-[20px] text-[#0d0d0d]">{risk.score}</span>
      <span className="font-segoe text-[11px] leading-[14px] text-[#565858]">{t(`risk.levels.${level}`)}</span>
    </span>
  );
}

/**
 * Risk register: one row per risk, riskiest first (the server sorts and
 * computes the score). The table scrolls horizontally inside its own
 * container — same pattern as CjmView — so it never widens the page on mobile.
 */
export function RiskRegisterView({ content }: RiskRegisterViewProps) {
  const { t } = useTranslation('methodologies');
  const risks = content.risks ?? [];

  const headClass = 'text-left font-manrope font-semibold text-[12px] leading-[16px] text-[#565858] uppercase p-[12px] align-bottom';
  const cellClass = 'align-top p-[12px] border-t border-[#eeeeee] font-segoe text-[14px] leading-[20px] text-[#0d0d0d]';

  return (
    <div className="flex flex-col gap-[12px]">
      <p className="font-segoe text-[13px] leading-[18px] text-[#565858]">{t('risk.legend')}</p>
      <div className="overflow-x-auto rounded-[16px] border border-[#eeeeee]">
        <table className="border-collapse w-full min-w-[880px]">
          <thead>
            <tr className="bg-[#f4f4f3]">
              <th className={`${headClass} w-[48px]`}>{t('risk.columns.id')}</th>
              <th className={`${headClass} min-w-[240px]`}>{t('risk.columns.title')}</th>
              <th className={headClass}>{t('risk.columns.category')}</th>
              <th className={`${headClass} text-center`} title={t('risk.columns.probabilityHint')}>{t('risk.columns.probability')}</th>
              <th className={`${headClass} text-center`} title={t('risk.columns.impactHint')}>{t('risk.columns.impact')}</th>
              <th className={`${headClass} text-center`}>{t('risk.columns.score')}</th>
              <th className={`${headClass} min-w-[220px]`}>{t('risk.columns.mitigation')}</th>
              <th className={headClass}>{t('risk.columns.owner')}</th>
            </tr>
          </thead>
          <tbody>
            {risks.map((risk) => (
              <tr key={risk.id}>
                <td className={`${cellClass} text-[#969696]`}>{risk.id}</td>
                <td className={cellClass}>
                  <p>{risk.title}</p>
                  <p className="mt-[6px] text-[13px] leading-[18px] text-[#565858]">
                    <span className="font-semibold">{t('risk.trigger')}:</span> {risk.trigger}
                  </p>
                </td>
                <td className={`${cellClass} whitespace-nowrap`}>
                  {t(`risk.categories.${risk.category}`, { defaultValue: risk.category })}
                </td>
                <td className={`${cellClass} text-center`}>{risk.probability}</td>
                <td className={`${cellClass} text-center`}>{risk.impact}</td>
                <td className={`${cellClass} text-center`}>
                  <ScoreBadge risk={risk} />
                </td>
                <td className={cellClass}>
                  <ul className="flex flex-col gap-[6px]">
                    {risk.mitigation.map((action, index) => (
                      <li key={index} className="pl-[14px] relative">
                        <span className="absolute left-0 top-[8px] w-[4px] h-[4px] rounded-full bg-[#2197f1]" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className={cellClass}>{risk.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
